import { frameAtTick, type Replay, type ReplayKill } from "@69ly/shared";

/**
 * Duels: every kill between the two teams, read with what led to it in the seconds before
 * (who hit first, damage both ways, flashes), the round's opening duel, and trades: a
 * death answered by a teammate killing the killer within a few seconds.
 */

/** A kill is traded when a teammate of the victim kills the killer within this. */
export const TRADE_SECONDS = 5;
/** How far back a duel's exchange is read. */
const DUEL_SECONDS = 4;
/** Game units in a metre, as the game counts distance in kill events. */
const UNITS_PER_METRE = 39.37;

export interface Duel {
  kill: ReplayKill;
  /** Index into `rounds`. */
  round: number;
  /** Seconds since the round's freeze time began. */
  at: number;
  killer: number;
  victim: number;
  /** The first of the two to hurt the other in the exchange, or -1 when neither did before the kill. */
  firstHit: number;
  /** Health damage of the killer to the victim, and of the victim to the killer, in the exchange. */
  damageDealt: number;
  damageTaken: number;
  /** Seconds from the killer's first hit on the victim to the kill (0 for a one-shot). */
  timeToKill: number;
  /** The victim was blinded at the moment of the kill; the killer was (the game's flag). */
  victimFlashed: boolean;
  killerFlashed: boolean;
  /** Metres apart. */
  distance: number;
  /** The round's first kill. */
  opening: boolean;
  /** This kill answered a teammate's death (the killer had killed them seconds before). */
  trade: boolean;
  /** The victim's death was answered by a teammate within TRADE_SECONDS. */
  traded: boolean;
}

const enemies = (r: Replay, a: number, b: number) => a >= 0 && b >= 0 && a !== b && r.players[a]?.team !== r.players[b]?.team;

/** Every kill between the two teams, round by round. */
export function duelsOf(r: Replay): Duel[] {
  const out: Duel[] = [];
  const P = r.players.length;
  r.rounds.forEach((round, index) => {
    const inside = (t: number) => t >= round.startTick && t <= round.postTick;
    const kills = r.kills.filter((k) => inside(k.tick) && enemies(r, k.killer, k.victim));
    const hurts = r.hurts.filter((h) => inside(h[0]));
    const window = DUEL_SECONDS * r.tickRate;
    const tradeWindow = TRADE_SECONDS * r.tickRate;
    kills.forEach((k, i) => {
      const exchange = hurts.filter((h) => h[0] <= k.tick && h[0] >= k.tick - window);
      const dealt = exchange.filter((h) => h[1] === k.killer && h[2] === k.victim);
      const taken = exchange.filter((h) => h[1] === k.victim && h[2] === k.killer);
      const first = [...dealt, ...taken].sort((a, b) => a[0] - b[0])[0];
      const blindLeft = r.blinds.filter((b) => b[2] === k.victim && b[0] <= k.tick).some((b) => b[0] + (b[3] / 100) * r.tickRate > k.tick);
      let distance = k.distance ?? 0;
      if (!distance) {
        // Older replays: from where both stood.
        const f = frameAtTick(r.columns.frameTicks, round.frameFrom, round.frameTo, k.tick);
        const a = f * P + k.killer;
        const b = f * P + k.victim;
        distance = Math.round((Math.hypot(r.columns.x[a] - r.columns.x[b], r.columns.y[a] - r.columns.y[b], r.columns.z[a] - r.columns.z[b]) / UNITS_PER_METRE) * 10) / 10;
      }
      const trade = kills.some((p) => p.victim !== k.killer && p.killer === k.victim && p.tick < k.tick && k.tick - p.tick <= tradeWindow && r.players[p.victim]?.team === r.players[k.killer]?.team);
      const traded = kills.some((n) => n.victim === k.killer && n.tick > k.tick && n.tick - k.tick <= tradeWindow && r.players[n.killer]?.team === r.players[k.victim]?.team);
      out.push({
        kill: k,
        round: index,
        at: (k.tick - round.startTick) / r.tickRate,
        killer: k.killer,
        victim: k.victim,
        firstHit: first ? first[1] : -1,
        damageDealt: Math.min(100, dealt.reduce((n, h) => n + h[3], 0)),
        damageTaken: Math.min(100, taken.reduce((n, h) => n + h[3], 0)),
        timeToKill: dealt.length ? Math.round(((k.tick - dealt[0][0]) / r.tickRate) * 100) / 100 : 0,
        victimFlashed: blindLeft,
        killerFlashed: k.blind,
        distance,
        opening: i === 0,
        trade,
        traded,
      });
    });
  });
  return out;
}

export interface DuelTotals {
  player: number;
  kills: number;
  deaths: number;
  openingWon: number;
  openingLost: number;
  /** Kills that answered a teammate's death. */
  trades: number;
  /** Deaths a teammate answered, and deaths left unanswered. */
  traded: number;
  untraded: number;
  headshots: number;
  /** Duels where they hit first, and won those. */
  firstHits: number;
  firstHitsWon: number;
}

export function duelTotals(r: Replay, duels: Duel[]): DuelTotals[] {
  const rows = r.players.map((_, player): DuelTotals => ({ player, kills: 0, deaths: 0, openingWon: 0, openingLost: 0, trades: 0, traded: 0, untraded: 0, headshots: 0, firstHits: 0, firstHitsWon: 0 }));
  for (const d of duels) {
    const k = rows[d.killer];
    const v = rows[d.victim];
    k.kills++;
    v.deaths++;
    if (d.kill.headshot) k.headshots++;
    if (d.opening) {
      k.openingWon++;
      v.openingLost++;
    }
    if (d.trade) k.trades++;
    if (d.traded) v.traded++;
    else v.untraded++;
    if (d.firstHit >= 0) {
      rows[d.firstHit].firstHits++;
      if (d.firstHit === d.killer) k.firstHitsWon++;
    }
  }
  return rows;
}

/** Kills of each player of one team against each of the other: grid[a][b] = a killed b. */
export function duelGrid(r: Replay, duels: Duel[]): Map<number, Map<number, number>> {
  const grid = new Map<number, Map<number, number>>();
  for (const d of duels) {
    const row = grid.get(d.killer) ?? grid.set(d.killer, new Map()).get(d.killer)!;
    row.set(d.victim, (row.get(d.victim) ?? 0) + 1);
  }
  return grid;
}
